import {DotLottiePlayer} from '@dotlottie/react-player'
import type {ImageField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {FeatureSlice} from 'prismicio-types'
import {memo} from 'react'
import {Button} from 'src/common/Button'
import type {Context} from 'src/common/types'

type BackgroundColorType = 'Beige/100' | 'Beige/200' | 'Moss White' | 'Moss Black' | 'Green/900'

const brightColors = ['Beige/100', 'Beige/200', 'Moss White']
const darkColors = ['Moss Black', 'Green/900']

const getTitleColor = (backgroundColor: BackgroundColorType) => ({
  'text-moss-black': brightColors.includes(backgroundColor),
  'text-moss-white': darkColors.includes(backgroundColor),
})

const getDescriptionColor = (backgroundColor: BackgroundColorType) => ({
  'text-grey-550': brightColors.includes(backgroundColor),
  'text-grey-135': darkColors.includes(backgroundColor),
})

const Feature = (props: {slice: FeatureSlice; context: Context}) => {
  const {linkResolver} = useLinkResolver()
  const backgroundColor = props.slice.primary.background_color

  const titleClasses = cn(
    "[&_>_br]:max-md:content-[''] [&_>_br]:max-md:after:content-['_']",
    getTitleColor(backgroundColor || 'Moss White'),
  )

  const descriptionClasses = cn(getDescriptionColor(backgroundColor || 'Moss White'), {
    'text-body': props.slice.variation !== 'reskin',
    'text-tg-body-m md:text-tg-body-l !text-moss-grey-500': props.slice.variation === 'reskin',
  })

  const isReskin = props.slice.variation === 'reskin'
  const isImageOnLeft = props.slice.primary.is_image_on_the_left

  return (
    <section
      className={cn(
        'scroll-mt-16',
        {'bg-beige-100': props.slice.primary.background_color === 'Beige/100'},
        {'bg-beige-200': props.slice.primary.background_color === 'Beige/200'},
        {'bg-moss-white': props.slice.primary.background_color === 'Moss White'},
        {'bg-moss-black text-moss-white': props.slice.primary.background_color === 'Moss Black'},
        {'bg-moss-green-900': props.slice.primary.background_color === 'Green/900'},
        isReskin ? 'py-8 md:py-12 lg:py-16' : 'grid grid-cols-main items-center gap-y-6 py-8 md:py-0',
      )}
      id={String(props.slice.primary.id_anchor)}
    >
      <div
        className={cn(
          isReskin
            ? 'mx-auto grid w-full max-w-[1200px] grid-cols-1 items-center gap-y-6 px-5 md:grid-cols-2 md:gap-x-10 md:px-10 lg:px-5 xl:px-0'
            : 'contents',
        )}
      >
        <div
          className={cn(
            isReskin
              ? cn({'md:order-2': !isImageOnLeft})
              : cn('col-[inner]', {
                  'md:col-end-[track-end_6]': isImageOnLeft,
                  'md:col-start-[track-start_7]': !isImageOnLeft,
                }),
          )}
          data-aos="fade-up"
          data-aos-offset="0"
        >
          <Asset
            variation={props.slice.variation}
            {...(props.slice.variation === 'lottieAnimation'
              ? {
                  lottie: props.slice.primary.lottie_animation,
                }
              : {
                  image: props.slice.primary.image,
                })}
          />
        </div>

        <div
          className={cn(
            'grid place-items-start gap-y-3',
            isReskin
              ? cn({
                  'md:order-1 md:max-w-[455px]': !isImageOnLeft,
                  'md:max-w-[455px] xl:max-w-lg md:pl-10': isImageOnLeft,
                })
              : cn('col-[inner] md:row-start-1', {
                  'md:col-end-[track-end_5]': !isImageOnLeft,
                  'md:col-start-[track-start_8]': isImageOnLeft,
                }),
          )}
          data-aos="fade-up"
          data-aos-offset="0"
        >
          <div className="flex flex-col gap-3">
            {isFilled.keyText(props.slice.primary.tag) && (
              <div
                className={cn('text-tg-body-s md:text-button-s font-semibold uppercase', {
                  'text-neon-550': props.slice.variation !== 'reskin',
                  'text-moss-blue':
                    props.slice.variation === 'reskin' &&
                    ('eyebrow_colour' in props.slice.primary ? props.slice.primary.eyebrow_colour : 'Blue') !== 'Grey',
                  'text-moss-grey-400':
                    props.slice.variation === 'reskin' &&
                    'eyebrow_colour' in props.slice.primary &&
                    props.slice.primary.eyebrow_colour === 'Grey',
                })}
              >
                {props.slice.primary.tag}
              </div>
            )}
            <PrismicRichText
              components={{
                heading2: ({children}) => <h2 className={`text-h3 ${titleClasses}`}>{children}</h2>,
                heading3: ({children}) => <h3 className={`text-h4 ${titleClasses}`}>{children}</h3>,
                paragraph: ({children}) => <h3 className={`text-h3 ${titleClasses}`}>{children}</h3>,
              }}
              field={props.slice.primary.title}
            />
          </div>

          <PrismicRichText
            components={{
              paragraph: ({children}) => <p className={descriptionClasses}>{children}</p>,
              hyperlink: ({children, node}) => (
                <Link
                  className={cn('inline transition-colors hover:text-7290f3', {
                    'text-inherit': props.slice.primary.stealth_links,
                    'text-7290f3': !props.slice.primary.stealth_links,
                  })}
                  href={node.data.url || linkResolver(node.data)}
                  {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                  {...(node.data.link_type === 'Web' && node.data.target === '_blank' && {rel: 'noopener noreferrer'})}
                >
                  {children}
                </Link>
              ),
              list: ({children}) => (
                <ul
                  className={cn('mt-[-16px] pt-6', {
                    'list-none flex flex-col gap-2': props.slice.variation === 'reskin',
                    'list-disc pl-5': props.slice.variation !== 'reskin',
                  })}
                >
                  {children}
                </ul>
              ),
              listItem: ({children}) => (
                <li
                  className={cn(descriptionClasses, {
                    'flex gap-3 items-start': props.slice.variation === 'reskin',
                  })}
                >
                  {props.slice.variation === 'reskin' && (
                    <div className="flex h-6 items-center">
                      <div className="h-[5px] w-[5px] min-w-[5px] rounded-[1px] bg-moss-grey-300" />
                    </div>
                  )}
                  <span>{children}</span>
                </li>
              ),
            }}
            field={props.slice.primary.description}
          />

          {props.slice.primary.link_label && props.slice.primary.link && (
            <Button
              href={linkResolver(props.slice.primary.link, 'primary-button-feature')}
              {...(isFilled.link(props.slice.primary.link) &&
                props.slice.primary.link.link_type === 'Web' && {
                  target: props.slice.primary.link.target,
                })}
              variant={brightColors.includes(backgroundColor || 'Moss White') ? 'outlineV2' : 'outlineV2-dark'}
              {...(isFilled.keyText(props.slice.primary.tracking_id) && {
                'data-mktg-id': props.slice.primary.tracking_id,
              })}
              data-page-name={props.context.pageUid}
            >
              {props.slice.primary.link_label}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

export default memo(Feature)

const Asset = ({
  image,
  lottie,
  variation,
}: {image?: ImageField; lottie?: LinkField} & Pick<FeatureSlice, 'variation'>) => {
  if (isFilled.link(lottie)) {
    return <DotLottiePlayer loop src={lottie.url!} autoplay className="overflow-hidden rounded-xl md:rounded-2xl" />
  } else {
    return (
      <PrismicNextImage
        field={image}
        quality={100}
        className={cn({
          'rounded-xl md:rounded-2xl': variation !== 'reskin',
          'rounded-lg': variation === 'reskin',
        })}
        sizes="(max-width: 640px) calc(100vw - 48px), (max-width: 768px) calc(100vw - 80px), (max-width: 1440px) 45vw, 548px"
      />
    )
  }
}
